import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { readyInterviewJobIds } from '@/lib/forkhire/interview/interviewReadiness';

/**
 * Returns the count of active jobs that have AI interviews enabled
 * but do NOT have published interview questions configured yet.
 */
export function useUnconfiguredInterviewJobs() {
  const { profile } = useAuth();
  const orgId = profile?.org_id;

  return useQuery({
    queryKey: ['unconfigured-interview-jobs', orgId],
    queryFn: async () => {
      // Get all active jobs for this org
      const { data: jobs, error: jobsErr } = await supabase
        .from('jobs')
        .select('id')
        .eq('org_id', orgId!)
        .eq('status', 'active');

      if (jobsErr || !jobs?.length) return 0;

      const jobIds = jobs.map(j => j.id);

      // Get interview configs that are published for these jobs
      const { data: configs } = await supabase
        .from('interview_configs')
        .select('job_id, status, questions_json')
        .in('job_id', jobIds)
        .eq('status', 'published');

      const configuredJobIds = readyInterviewJobIds(configs);
      const unconfiguredCount = jobIds.filter(id => !configuredJobIds.has(id)).length;

      return unconfiguredCount;
    },
    enabled: !!orgId,
    staleTime: 3 * 60 * 1000,
  });
}
